// src/pages/reports/MasterOutlist/ReportsMasterTable.tsx
import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TableSortLabel,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  ListItemText,
  Checkbox,
  ListSubheader,
} from "@mui/material";
import PaginationReports from "../../brands/PaginationReports";
import { exportMasterOutletList, master_outlet_list_v1 } from "../api";
import Export from "../../../../src/assests/images/export.svg";
import Sorting from "../../../../src/assests/images/Sorting.svg";
import { getToken } from "../../../utils/utils";

// --- columns list (labels/values used by backend & normalized rows) ---
const columns = [
  "User ID",
  "PMI Entity Code",
  "User First Name",
  "User Last Name",
  "Outlet Name",
  "Last Updated On",
  "City",
  "Status",
  "User Salesforce ID",
] as const;

// Defaults (first 6 like Rewards)
const DEFAULT_VISIBLE: ColumnLabel[] = [...columns].slice(0, 6);
const DEFAULT_SET = new Set<ColumnLabel>(DEFAULT_VISIBLE);

// If user clears everything, restore defaults
const withDefaultsIfEmpty = (arr: ColumnLabel[]) =>
  arr && arr.length ? arr : [...DEFAULT_VISIBLE];

type ColumnLabel = (typeof columns)[number];
type Row = Record<string, any>;

interface ReportsMasterTableProps {
  startDate?: Date | null;
  endDate?: Date | null;
  searchQuery?: string;
  appliedFilters?: {
    locations?: string[] | null;
    outletTypes?: string[] | null;
    outletNames?: string[] | null;
    pmiEntityCodes?: string[] | null;
    status?: string[] | null;
    lastloginStart?: string | null;
    lastloginEnd?: string | null;
    user_salesforce_id?: string[] | null;
    backendFilters?: { [k: string]: any } | null;
    [k: string]: any;
  } | null;
}

// --- Rewards-style custom arrow for MUI Select ---
const CustomArrow: React.FC<{ open: boolean; onClick: () => void }> = ({ open, onClick }) => (
  <span
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    style={{
      display: "inline-block",
      width: 11,
      right: "10px",
      height: "8px",
      borderRight: "2px solid #000",
      borderBottom: "2px solid #000",
      transform: open ? "rotate(-135deg)" : "rotate(45deg)",
      transition: "transform 0.2s ease",
      marginLeft: "20px",
      position: "relative",
      cursor: "pointer",
      pointerEvents: "auto",
    }}
  />
);

const ReportsMasterTable: React.FC<ReportsMasterTableProps> = ({
  startDate,
  endDate,
  searchQuery,
  appliedFilters = null,
}) => {
  // data
  const [rows, setRows] = useState<Row[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // paging
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10); // 🔹 dropdown-controlled
  const totalPages = Math.ceil(totalRows / itemsPerPage);

  // sorting
  const [orderBy, setOrderBy] = useState<ColumnLabel>("User ID");
  const [order, setOrder] = useState<"asc" | "desc">("asc");

  // filters mirrored locally for payload build
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedOutletTypes, setSelectedOutletTypes] = useState<string[]>([]);
  const [selectedOutletNames, setSelectedOutletNames] = useState<string[]>([]);
  const [selectedPmiEntities, setSelectedPmiEntities] = useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string[]>([]);
  const [selectedUserSfa, setSelectedUserSfa] = useState<string[]>([]); // ✅ NEW
  const [localDateRange, setLocalDateRange] = useState<[Date | null, Date | null]>([null, null]);

  // export
  const [exporting, setExporting] = useState(false);

  // column picker (Rewards-style)
  const allColumns: ColumnLabel[] = [...columns];
  const [visibleColumns, setVisibleColumns] = useState<ColumnLabel[]>(DEFAULT_VISIBLE);
  const [tempSelectedColumns, setTempSelectedColumns] = useState<ColumnLabel[]>(DEFAULT_VISIBLE);
  const [colSelectOpen, setColSelectOpen] = useState(false);
  const [colSearch, setColSearch] = useState("");
  const colSearchRef = useRef<HTMLInputElement | null>(null);

  const filteredColumns = React.useMemo(() => {
    const q = colSearch.trim().toLowerCase();
    if (!q) return allColumns;
    return allColumns.filter((c) => c.toLowerCase().includes(q));
  }, [allColumns, colSearch]);

  const allFilteredSelected =
    filteredColumns.length > 0 && filteredColumns.every((c) => tempSelectedColumns.includes(c));

  const toggleSelectAllOnFiltered = () => {
    if (allFilteredSelected) {
      setTempSelectedColumns((prev) => {
        const next = prev.filter(
          (p) => !filteredColumns.includes(p) || DEFAULT_SET.has(p)
        );
        DEFAULT_VISIBLE.forEach((d) => {
          if (!next.includes(d)) next.push(d);
        });
        return next;
      });
    } else {
      setTempSelectedColumns((prev) =>
        Array.from(new Set<ColumnLabel>([...prev, ...filteredColumns, ...DEFAULT_VISIBLE]))
      );
    }
  };

  // abort control
  const fetchAbortRef = useRef<AbortController | null>(null);

  // hydrate from appliedFilters
  useEffect(() => {
    if (!appliedFilters) {
      setSelectedLocations([]);
      setSelectedOutletTypes([]);
      setSelectedOutletNames([]);
      setSelectedPmiEntities([]);
      setSelectedStatus([]);
      setSelectedUserSfa([]); // ✅ NEW
      setLocalDateRange([null, null]);
      setPage(1);
      return;
    }

    const bf = appliedFilters.backendFilters ?? {};
    const outletCityFromBF = Array.isArray((bf as any).outlet_city) ? ((bf as any).outlet_city as string[]) : [];

    setSelectedLocations(Array.isArray(appliedFilters.locations) ? appliedFilters.locations : outletCityFromBF);
    setSelectedOutletTypes(Array.isArray(appliedFilters.outletTypes) ? appliedFilters.outletTypes : []);
    setSelectedOutletNames(
      Array.isArray(appliedFilters.outletNames)
        ? appliedFilters.outletNames
        : Array.isArray((appliedFilters as any).outlet_salesforce_id)
        ? ((appliedFilters as any).outlet_salesforce_id as string[])
        : []
    );
    setSelectedPmiEntities(
      Array.isArray(appliedFilters.pmiEntityCodes)
        ? appliedFilters.pmiEntityCodes
        : Array.isArray((appliedFilters as any).pmi_entity_code)
        ? ((appliedFilters as any).pmi_entity_code as string[])
        : []
    );
    setSelectedStatus(Array.isArray(appliedFilters.status) ? appliedFilters.status : []);
    setSelectedUserSfa(
      Array.isArray((appliedFilters as any).user_salesforce_id)
        ? ((appliedFilters as any).user_salesforce_id as string[])
        : []
    ); // ✅ NEW

    const start = appliedFilters.lastloginStart ? new Date(appliedFilters.lastloginStart) : null;
    const end = appliedFilters.lastloginEnd ? new Date(appliedFilters.lastloginEnd) : null;
    setLocalDateRange([start, end]);

    setPage(1);
  }, [appliedFilters]);

  // helpers
  const formatDateForPayload = (d: Date | null | undefined) => {
    if (!d) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  // fetch data
  useEffect(() => {
    const fetchData = async () => {
      if (fetchAbortRef.current) {
        try {
          fetchAbortRef.current.abort();
        } catch {}
      }
      const controller = new AbortController();
      fetchAbortRef.current = controller;

      setIsLoading(true);
      try {
        const token = getToken();
        const [localStart, localEnd] = localDateRange;

        const requestPayload: any = {
          page,
          limit: itemsPerPage, // 🔹 dynamic page size
          ...(searchQuery ? { search: searchQuery } : {}),
          ...(startDate ? { outlet_last_modified_date_from: formatDateForPayload(startDate) } : {}),
          ...(endDate ? { outlet_last_modified_date_to: formatDateForPayload(endDate) } : {}),

          // arrays on top-level
          ...(selectedLocations.length ? { locations: selectedLocations } : {}),
          ...(selectedOutletTypes.length ? { outlet_category: selectedOutletTypes } : {}),
          ...(selectedOutletNames.length ? { outletNames: selectedOutletNames } : {}),
          ...(selectedPmiEntities.length ? { pmi_entity_code: selectedPmiEntities } : {}),
          ...(selectedStatus.length ? { status: selectedStatus, user_status: selectedStatus } : {}),
          ...(selectedUserSfa.length ? { user_salesforce_id: selectedUserSfa } : {}), // ✅ NEW
          ...(localStart ? { lastLogin_from: formatDateForPayload(localStart) } : {}),
          ...(localEnd ? { lastLogin_to: formatDateForPayload(localEnd) } : {}),

          // filters wrapper (backend merges)
          filters: {
            ...(selectedLocations.length ? { locations: selectedLocations } : {}),
            ...(selectedOutletTypes.length ? { outlet_category: selectedOutletTypes } : {}),
            ...(selectedOutletNames.length ? { outletNames: selectedOutletNames } : {}),
            ...(selectedPmiEntities.length ? { pmi_entity_code: selectedPmiEntities } : {}),
            ...(selectedStatus.length ? { status: selectedStatus, user_status: selectedStatus } : {}),
            ...(selectedUserSfa.length ? { user_salesforce_id: selectedUserSfa } : {}), // ✅ NEW
            ...(localStart ? { lastLogin_from: formatDateForPayload(localStart) } : {}),
            ...(localEnd ? { lastLogin_to: formatDateForPayload(localEnd) } : {}),
          },
        };

        const response = await master_outlet_list_v1(requestPayload, token);
        const jsonBody = response && response.data ? response.data : response;
        const resultsArray =
          jsonBody?.data?.results ?? jsonBody?.results ?? jsonBody?.data ?? [];
        const totalRowsValue = jsonBody?.data?.total_rows ?? jsonBody?.total_rows ?? 0;

        const normalized = (Array.isArray(resultsArray) ? resultsArray : []).map((r: any) => ({
          "User ID": r["User ID"] ?? r.user_id ?? r.UserId ?? r.userId ?? "",
          "User Salesforce ID":
            r["User Salesforce ID"] ?? r.user_salesforce_id ?? r.userSalesforceId ?? "",
          "PMI Entity Code": r["PMI Entity Code"] ?? r.pmi_entity_code_retailer ?? r.pmi ?? "",
          "User First Name":
            r["User First Name"] ?? r.user_first_name ?? r.first_name ?? r.firstName ?? "",
          "User Last Name":
            r["User Last Name"] ?? r.user_last_name ?? r.last_name ?? r.lastName ?? "",
          "Outlet Name": r["Outlet Name"] ?? r.outlet_name ?? r.name ?? "",
          City: r["City"] ?? r.outlet_city ?? r.city ?? "",
          Status: r["Status"] ?? r.user_status ?? r.status ?? "",
          "Last Updated On":
            r["Last Updated On"] ?? r.last_modified_date ?? r.outlet_last_modified_date ?? "",
        }));

        if (!controller.signal.aborted) {
          setRows(normalized);
          setTotalRows(Number(totalRowsValue) || normalized.length);
        }
      } catch (err: any) {
        if (err?.name !== "AbortError") {
          console.error("Failed to fetch master outlet list", err);
          setRows([]);
          setTotalRows(0);
        }
      } finally {
        if (fetchAbortRef.current === controller) fetchAbortRef.current = null;
        setIsLoading(false);
      }
    };

    fetchData();

    return () => {
      if (fetchAbortRef.current) {
        try {
          fetchAbortRef.current.abort();
        } catch {}
        fetchAbortRef.current = null;
      }
    };
  }, [
    startDate,
    endDate,
    searchQuery,
    page,
    itemsPerPage, // 🔹 refetch when size changes
    selectedLocations,
    selectedOutletTypes,
    selectedOutletNames,
    selectedPmiEntities,
    selectedStatus,
    selectedUserSfa, // ✅ NEW
    localDateRange,
  ]);

  // sorting
  const handleSort = (property: ColumnLabel) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // --- helpers for proper sorting (numeric + date aware) ---
  const parseDateMs = (v: any): number => {
    if (v == null || v === "") return Number.NaN;
    const d = new Date(v);
    return isNaN(d.getTime()) ? Number.NaN : d.getTime();
  };

  const isNumericLike = (v: any) =>
    typeof v === "number" || (typeof v === "string" && /^\d+$/.test(v.trim()));

  const getSortKey = (row: Row, col: ColumnLabel): number | string => {
    const val = row[col];

    if (col === "Last Updated On") {
      const t = parseDateMs(val);
      return isNaN(t) ? Number.NEGATIVE_INFINITY : t;
    }

    if (col === "User ID" || col === "User Salesforce ID") {
      if (typeof val === "number") return val;
      if (isNumericLike(val)) return Number(val);
      return Number.NEGATIVE_INFINITY;
    }

    return val == null ? "" : String(val).toLowerCase();
  };

  const cmp = (a: any, b: any) => (a < b ? -1 : a > b ? 1 : 0);

  const sortedRows = React.useMemo(() => {
    const copy = [...rows];
    copy.sort((ra, rb) => {
      const ka = getSortKey(ra, orderBy);
      const kb = getSortKey(rb, orderBy);
      const base = cmp(ka, kb);
      return order === "asc" ? base : -base;
    });
    return copy;
  }, [rows, orderBy, order]);

  // export (only filtered values; mirror fetch payload, export all rows)
  const handleExport = async () => {
    try {
      setExporting(true);

      const exportColumns = visibleColumns;
      if (exportColumns.length === 0) {
        console.warn("[EXPORT FE] No columns selected to export");
        setExporting(false);
        return;
      }

      const [s, e] = localDateRange;
      const params: any = {
        limit: "all",
        export: true,
        columns: exportColumns.join(","), // backend expects headers in order

        // keep parity with fetch payload
        ...(searchQuery && { search: searchQuery }),
        ...(startDate && { outlet_last_modified_date_from: formatDateForPayload(startDate) }),
        ...(endDate && { outlet_last_modified_date_to: formatDateForPayload(endDate) }),

        // top-level arrays
        ...(selectedLocations.length ? { locations: selectedLocations } : {}),
        ...(selectedOutletTypes.length ? { outlet_category: selectedOutletTypes } : {}),
        ...(selectedOutletNames.length ? { outletNames: selectedOutletNames } : {}),
        ...(selectedPmiEntities.length ? { pmi_entity_code: selectedPmiEntities } : {}),
        ...(selectedStatus.length ? { status: selectedStatus, user_status: selectedStatus } : {}),
        ...(selectedUserSfa.length ? { user_salesforce_id: selectedUserSfa } : {}), // ✅ NEW
        ...(s ? { lastLogin_from: formatDateForPayload(s) } : {}),
        ...(e ? { lastLogin_to: formatDateForPayload(e) } : {}),

        // filters wrapper too (some backends read this exclusively for export)
        filters: {
          ...(selectedLocations.length ? { locations: selectedLocations } : {}),
          ...(selectedOutletTypes.length ? { outlet_category: selectedOutletTypes } : {}),
          ...(selectedOutletNames.length ? { outletNames: selectedOutletNames } : {}),
          ...(selectedPmiEntities.length ? { pmi_entity_code: selectedPmiEntities } : {}),
          ...(selectedStatus.length ? { status: selectedStatus, user_status: selectedStatus } : {}),
          ...(selectedUserSfa.length ? { user_salesforce_id: selectedUserSfa } : {}), // ✅ NEW
          ...(s ? { lastLogin_from: formatDateForPayload(s) } : {}),
          ...(e ? { lastLogin_to: formatDateForPayload(e) } : {}),
        },

        ...(orderBy ? { sort_by: orderBy } : {}),
        ...(order ? { sort_dir: order } : {}),
      };

      const token = getToken();
      const blobData = await exportMasterOutletList(params, token);
      const blob = blobData instanceof Blob ? blobData : new Blob([blobData], { type: "text/csv" });

      const filename = `master_outlet_list_${new Date().toISOString().slice(0, 10)}.csv`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setExporting(false);
    }
  };

  const toSingleLine = (v: any) => String(v).replace(/\s*\n+\s*/g, " ").trim();

  // UI
  return (
    <Box
      sx={{
        background: "#fff",
        borderRadius: "10px",
        border: "1px solid #cfcfcf",
        margin: "30px 32px 100px",
      }}
    >
      {/* Header */}
      <Box sx={{ padding: "25px", display: "flex", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: "16px", fontWeight: "600", marginTop: "10px" }}>
          Total {totalRows} Users
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center" }}>
          {/* Column Picker */}
          <Box sx={{ marginRight: "20px" }}>
            <Tooltip
              title={
                tempSelectedColumns.length ? tempSelectedColumns.join(", ") : "Select Columns"
              }
              placement="top"
              arrow
              PopperProps={{
                modifiers: [{ name: "offset", options: { offset: [0, 10] } }],
              }}
            >
              <FormControl sx={{ minWidth: 240, width: 240 }}>
                <Select
                  multiple
                  displayEmpty
                  value={tempSelectedColumns}
                  open={colSelectOpen}
                  onOpen={() => setColSelectOpen(true)}
                  onClose={() => {
                    setColSelectOpen(false);
                    const next = withDefaultsIfEmpty(tempSelectedColumns);
                    setTempSelectedColumns(next);
                    setVisibleColumns(next);
                    setColSearch("");
                  }}
                  onChange={(e) => {
                    const val = e.target.value;
                    const arr = typeof val === "string" ? (val as string).split(",") : (val as ColumnLabel[]);
                    if (Array.isArray(arr) && (arr as any[]).includes("__ALL__")) {
                      toggleSelectAllOnFiltered();
                      return;
                    }
                    const next = withDefaultsIfEmpty(Array.isArray(arr) ? (arr as ColumnLabel[]) : []);
                    setTempSelectedColumns(next);
                  }}
                  renderValue={(selected) => {
                    if ((selected as string[]).length === 0) return "Select Columns";
                    return (selected as string[]).join(", ");
                  }}
                  inputProps={{ "aria-label": "columns-select" }}
                  IconComponent={() => (
                    <CustomArrow open={colSelectOpen} onClick={() => setColSelectOpen((v) => !v)} />
                  )}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        minWidth: 240,
                        paddingTop: 0,
                        paddingBottom: 0,
                        overflow: "overlay",
                        borderRadius: 10,
                        background: "#fff",
                        boxShadow: "none",
                      },
                    },
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                    transformOrigin: { vertical: "top", horizontal: "left" },
                    disableAutoFocusItem: true,
                    MenuListProps: {
                      style: {
                        paddingTop: 0,
                        paddingBottom: 0,
                        maxHeight: "250px",
                        overflowY: "auto",
                      },
                      onKeyDown: (e: React.KeyboardEvent) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          e.stopPropagation();
                          (e.nativeEvent as KeyboardEvent).stopImmediatePropagation?.();
                          setTimeout(() => colSearchRef.current?.focus(), 0);
                        }
                      },
                    },
                  }}
                  sx={{
                    minWidth: 240,
                    height: "40px",
                    borderRadius: "10px",
                    backgroundColor: "#fff",
                    marginRight: "8px",
                    width: "240px",
                    position: "relative",
                    "& .MuiSelect-icon": { display: "none" },
                    "&:hover, &.Mui-focused, &.Mui-focusVisible": {
                      background: "transparent !important",
                      boxShadow: "none !important",
                      outline: "none !important",
                    },
                  }}
                >
                  <ListSubheader
                    sx={{
                      bgcolor: "white",
                      p: 1,
                      display: "flex",
                      alignItems: "center",
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        px: 1,
                        py: 0.5,
                        border: "1px solid #ccc",
                        borderRadius: 1,
                        bgcolor: "transparent",
                      }}
                      onClick={(ev) => ev.stopPropagation()}
                    >
                      <input
                        ref={colSearchRef}
                        value={colSearch}
                        onChange={(e) => setColSearch(e.target.value)}
                        placeholder="Search columns..."
                        style={{
                          width: "100%",
                          border: "none",
                          outline: "none",
                          fontSize: 14,
                          background: "transparent",
                          padding: "4px",
                          height: "40px",
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          (e.nativeEvent as KeyboardEvent).stopImmediatePropagation?.();
                        }}
                      />
                      {colSearch && (
                        <Box
                          component="span"
                          sx={{ cursor: "pointer", fontWeight: "bold", fontSize: 18, lineHeight: 1, px: 1 }}
                          onClick={(ev) => {
                            ev.stopPropagation();
                            setColSearch("");
                            setTimeout(() => colSearchRef.current?.focus(), 0);
                          }}
                        >
                          ×
                        </Box>
                      )}
                    </Box>
                  </ListSubheader>

                  <MenuItem
                    dense
                    value="__ALL__"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      toggleSelectAllOnFiltered();
                    }}
                  >
                    <Checkbox checked={allFilteredSelected} />
                    <ListItemText primary="Select All" />
                  </MenuItem>

                  {filteredColumns.length ? (
                    filteredColumns.map((name) => (
                      <MenuItem key={name} value={name}>
                        <Checkbox checked={tempSelectedColumns.includes(name)} />
                        <ListItemText primary={name} />
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled dense>
                      <ListItemText primary="No results" />
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Tooltip>
          </Box>

          <Button
            variant="contained"
            disabled={exporting}
            sx={{
              textTransform: "capitalize",
              backgroundColor: "#fff",
              fontSize: "14px",
              fontWeight: "500",
              height: "40px",
              padding: "16px",
              borderRadius: "10px",
              color: "#C8172F",
              border: "1px solid #C8172F",
              boxShadow: "none",
            }}
            startIcon={<img src={Export} alt="export" />}
            onClick={handleExport}
          >
            {exporting ? "Exporting..." : "Export CSV"}
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer
        component={Paper}
        sx={{
          borderTop: "1px solid #d3d3d3",
          boxShadow: "none",
          maxWidth: "100%",
          overflowX: "auto",
          "& td, & th": {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: 300,
            minWidth: 150,
          },
        }}
      >
        <Table sx={{ minWidth: visibleColumns.length * 150, tableLayout: "fixed" }}>
          <TableHead sx={{ background: "#EEF4FF" }}>
            <TableRow>
              {visibleColumns.map((col) => (
                <TableCell
                  key={col}
                  sx={{
                    minWidth: 150,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: 300,
                  }}
                >
                  <TableSortLabel
                    active={orderBy === col}
                    direction={orderBy === col ? order : "asc"}
                    onClick={() => handleSort(col)}
                    IconComponent={() => (
                      <img src={Sorting} alt="sort" style={{ width: 16, height: 16 }} />
                    )}
                  >
                    {col}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedRows.length > 0 ? (
              sortedRows.map((row, idx) => (
                <TableRow key={row["User ID"] ?? idx}>
                  {visibleColumns.map((col) => {
                    const raw = row[col];
                    let display: React.ReactNode = "-";
                    if (raw !== null && raw !== undefined && raw !== "") {
                      if (col === "Last Updated On") {
                        const d = new Date(raw);
                        if (!isNaN(d.getTime())) {
                          const datePart = d
                            .toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })
                            .replace(/ /g, "-");
                          const timePart = d.toLocaleTimeString("en-GB", {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: false,
                          });
                          display = `${datePart} ${timePart}`;
                        } else {
                          display = String(raw);
                        }
                      } else {
                        display = String(raw);
                      }
                    }
                    return (
                      <TableCell key={col} title={typeof display === "string" ? display : undefined}>
                        {typeof display === "string" ? toSingleLine(display) : display}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={visibleColumns.length} align="center">
                  {isLoading ? "Loading..." : "No data available"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination + rows-per-page */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
        }}
      >
        <Box sx={{ width: 200 }} />
        <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
          <PaginationReports page={page} limit={itemsPerPage} totalPages={totalPages} setPage={setPage} />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" sx={{ color: "#555" }}>
            Rows per page
          </Typography>
          <FormControl size="small" sx={{ minWidth: 92 }}>
            <Select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setPage(1); // reset to first page on size change
              }}
              sx={{ borderRadius: "8px", height: 36 }}
            >
              {[10, 25, 50, 100, 200].map((n) => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
    </Box>
  );
};

export default ReportsMasterTable;
